import { ExclamationCircleOutlined } from "@ant-design/icons";
import {
	Avatar,
	Button,
	Drawer,
	Empty,
	Flex,
	Modal,
	Popconfirm,
	Space,
} from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import elementalSvg from "../../assets/image/elemental 'e'.svg";
import { HomeContext } from "../../containers/Home";
import {
	addDeliverableComment,
	addTaskComment,
	deleteComment,
	editComment,
	getDeliverableComments,
	getTaskComments,
} from "../../services/api-server/discussion";
import { GetAntIcon } from "../../utils/ant_icons";
import Emitter from "../../utils/emitter";
import { emailsEqual } from "../../utils/utils";
import TextEditor from "../TextEditor";
import { getUser } from "../../services/api-server/user";

interface TextEditorHandle {
	clearEditor: () => void; // Method to clear the editor
	getEditor: () => {
		root: {
			innerHTML: string;
		};
	};
}

interface DiscussionButtonProps {
	setDiscussionOpen: (open: boolean) => void;
}

interface taskI {
	_id: string;
	task_name: string;
	task_number: string;
	closed: boolean;
	status: string;
}

interface deliverableI {
	_id: string;
	deliverable_name: string;
	status: string;
}

function DiscussionButton({ setDiscussionOpen }: DiscussionButtonProps) {
	return (
		<Button
			type="link"
			icon={GetAntIcon("comment")}
			onClick={() => setDiscussionOpen(true)}
			style={{ padding: 0 }}
		>
			Discussion
		</Button>
	);
}

interface CommentI {
	_id: string;
	user: string;
	created_on: string;
}

function CommenterSection({ comment }: { comment?: CommentI }) {
	const { userInfo }: any = useContext(HomeContext);
	const [user, setUser] = useState<any>(null);

	const searchUser = (email: string) => {
		getUser(email).then((user: any) => {
			setUser(user[0]);
		});
	};

	useEffect(() => {
		if (comment) {
			searchUser(comment.user);
		}
	}, [comment]);

	return (
		<div className="commenter-section">
			<Avatar
				size={20}
				icon={GetAntIcon("user")}
				src={user?.profile_picture ?? userInfo?.user?.profile_picture}
			/>

			{comment ? (
				<>
					{emailsEqual(userInfo?.user?.email, comment.user) ? (
						<Link to={`/profile`}>
							<span>{user?.name}</span>
						</Link>
					) : (
						<span>{user?.name}</span>
					)}

					<span className="comment-date-time">
						{new Date(comment.created_on)
							.toLocaleString("en-GB", {
								day: "numeric",
								month: "short",
								year: "numeric",
								hour: "2-digit",
								minute: "2-digit",
								second: "2-digit",
								hour12: true,
							})
							.replace(",", "")
							.replace(" am", " AM")
							.replace(" pm", " PM")}
					</span>
				</>
			) : (
				<Link to={`/profile`}>
					<span>{userInfo?.user?.name}</span>
				</Link>
			)}
		</div>
	);
}

function LeaveConfirmationModal({
	open,
	setOpen,
	discussionOpen,
	setDiscussionOpen,
}: {
	open: boolean;
	setOpen: (open: boolean) => void;
	discussionOpen: boolean;
	setDiscussionOpen: (open: boolean) => void;
}) {
	return (
		<Modal
			open={open}
			closable={false}
			maskClosable={false}
			cancelButtonProps={{ ghost: true }}
			cancelText="Leave"
			okText="Continue editing"
			okType="default"
			rootClassName="leave-changes-modal"
			onCancel={() => {
				setOpen(false);
				setDiscussionOpen(false);
			}}
			onOk={() => {
				setOpen(false);
			}}
		>
			<Space>
				<Flex align="center" gap={20}>
					<ExclamationCircleOutlined style={{ color: "grey" }} />
					Leave without saving? Changes will not be saved.
				</Flex>
			</Space>
		</Modal>
	);
}

function EditCommentButton({
	handleEdit,
	newCommentTrimmed,
}: {
	handleEdit: () => void;
	newCommentTrimmed: string;
}) {
	return (
		<Button
			size="small"
			shape="circle"
			icon={GetAntIcon("edit")}
			style={{ border: "none" }}
			onClick={handleEdit}
			disabled={newCommentTrimmed.length > 0}
		/>
	);
}

function DeleteCommentButton({ handleDelete }: { handleDelete: () => void }) {
	return (
		<Popconfirm
			title={
				<div
					style={{
						maxWidth: "300px",
						padding: "10px",
					}}
				>
					Permanently delete this comment?
					<br />
					This action cannot be undone.
				</div>
			}
			icon={
				<ExclamationCircleOutlined
					style={{
						color: "grey",
					}}
				/>
			}
			okText="Delete"
			okType="danger"
			cancelButtonProps={{
				ghost: true,
			}}
			onConfirm={handleDelete}
		>
			<Button
				size="small"
				shape="circle"
				icon={GetAntIcon("delete")}
				// onClick={() =>
				// handleDeleteInit(comment._id)
				// }
			/>
		</Popconfirm>
	);
}

function CommentBox({ comment }: { comment: any }) {
	return (
		<div className="comment-box">
			{comment.comment
				.split(/(<p[^>]*>.*?<\/p>)/g)
				.map((line: any, index: any) => {
					const indentMatch = line.match(/ql-indent-(\d+)/);
					const paddingLeft = indentMatch
						? `${parseInt(indentMatch[1]) * 39}px`
						: "0";

					return (
						<div
							key={index}
							style={{ paddingLeft }}
							dangerouslySetInnerHTML={{
								__html: line,
							}}
						/>
					);
				})}
		</div>
	);
}

interface DeliverablesDiscussionModalProps {
	title: string;
	task: taskI;
	deliverable?: deliverableI;
}

function DeliverablesDiscussionModal({
	title,
	task,
	deliverable,
}: DeliverablesDiscussionModalProps) {
	const { userInfo, currentTenantKey }: any = useContext(HomeContext);

	const [discussionOpen, setDiscussionOpen] = useState<boolean>(false);
	const [newComment, setNewComment] = useState<string>("");
	const [newCommentTrimmed, setNewCommentTrimmed] = useState<string>("");
	const [comments, setComments] = useState<any[]>([]);
	const [hideButtons, setHideButtons] = useState<any>(true);

	// Add comment handling
	const [addButtonDisabled, setAddButtonDisabled] = useState<any>(true);
	// Edit comment handling
	const [editButtonDisabled, setEditButtonDisabled] = useState<any>(true);
	const [editCommentClick, setEditCommentClick] = useState<any>(false);
	const [editCommentID, setEditCommentID] = useState<any>(null);
	const [editCommentContent, setEditCommentContent] = useState<string>("");
	const [commentCompare, setCommentCompare] = useState<string>("");
	// Delete comment handling
	const [deleteCommentClick, setDeleteCommentClick] = useState<any>(false);
	const [deleteCommentID, setDeleteCommentID] = useState<any>(null);
	// Drawer container reference
	//   const drawerRef = useRef<HTMLDivElement>(null);
	// Add comment container reference
	const containerRef = useRef<HTMLDivElement>(null);
	// Text editor reference
	const editorRef = useRef<TextEditorHandle | null>(null);

	useEffect(() => {
		const editorContent = editorRef.current?.getEditor()?.root.innerHTML || ""; // Get HTML content
		const textContent = editorContent.replace(/<[^>]+>/g, ""); // Remove all HTML tags
		const trimmedContent = textContent.trim(); // Trim whitespace
		setNewCommentTrimmed(trimmedContent);

		const isCommentValid = trimmedContent.length > 0; // Check if there is any text
		setAddButtonDisabled(!isCommentValid); // Enable or disable button based on text presence

		if (isCommentValid) {
			setHideButtons(false);
		}

		const clickOutsideAddComment = (event: MouseEvent) => {
			// if the click happens outside the `containerRef`, `hideButtons` is set to true
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node) &&
				!isCommentValid
			) {
				setHideButtons(true);
			}
		};

		document.addEventListener("mousedown", clickOutsideAddComment);
		return () => {
			document.removeEventListener("mousedown", clickOutsideAddComment);
		};
	}, [newComment, containerRef]); // Run this effect when newComment changes

	const loadComments = (tenantKey: any, taskId: any, deliverableId: any) => {
		// Call only the necessary API based on deliverable
		const commentApi = deliverable?._id
			? getDeliverableComments(tenantKey, taskId, deliverableId)
			: getTaskComments(tenantKey, taskId);

		commentApi
			.then((data: any) => {
				if (Array.isArray(data)) {
					setComments(data.reverse());
					console.log("Loaded comments:", data);
				} else {
					setComments([]);
				}
			})
			.catch((e) => {
				console.error("Error loading comments:", e);
			});
	};

	useEffect(() => {
		if (currentTenantKey && task._id) {
			loadComments(currentTenantKey, task._id, deliverable?._id);
		}
	}, [
		(currentTenantKey && task._id) || (currentTenantKey && deliverable),
		deliverable?._id,
	]);

	// Add comment handling
	const handleAddComment = async () => {
		// Insert `newComment` from `TextEditor` into `originalComment`
		const originalComment = newComment;

		// Check for empty comment (only whitespace)
		if (originalComment.trim() === "") return;

		try {
			const addCommentApi = await (deliverable
				? addDeliverableComment(
						currentTenantKey,
						task._id,
						deliverable?._id,
						userInfo.user.email,
						originalComment
					)
				: addTaskComment(
						currentTenantKey,
						task._id,
						userInfo.user.email,
						originalComment
					));

			// Update comments state
			setComments((prev) => [addCommentApi, ...prev]);
			setNewComment(""); // Clear the input field
			editorRef.current?.clearEditor(); // Clear the TextEditor content

			console.log("Comment added successfully");

			Emitter.emit("alert", {
				type: "success",
				message: "Comment has been successfully saved.",
				description: "",
				top: true,
				closeable: false,
				timeout: 3000,
			});
		} catch (error) {
			console.error("Error adding comment:", error);
		}
	};

	const handleEditInit = (editCommentID: any, editCommentContent: any) => {
		// Handle the Clicking of Edit Button
		setEditCommentClick(!editCommentClick);
		// Handle the storing of Selected Comment ID
		setEditCommentID(editCommentID);
		// Handle the storing of Selected Comment
		setEditCommentContent(editCommentContent);
		// Handle the storing of New Comment
		setCommentCompare(editCommentContent);
	};

	const handleEditComment = async (commentId: any, editCommentContent: any) => {
		// Insert `updatedComment` from `TextEditor` into `originalComment`
		const updatedComment = editCommentContent;

		// Check for empty comment (only whitespace)
		if (updatedComment.trim() === "") {
			return;
		}

		try {
			// Call the `editComment` API with the updated comment data
			const data = await editComment(
				currentTenantKey,
				commentId,
				updatedComment
			);

			// Update the comments state by replacing the edited comment
			setComments((prev) =>
				prev.map((comment) => {
					return comment._id === commentId ? data : comment;
				})
			);

			setNewComment("");
			/* HandleEditInit */
			setEditCommentClick(false);
			setEditCommentID(null);
			setCommentCompare("");

			editorRef.current?.clearEditor(); // Clear the TextEditor content
		} catch (error) {
			console.error("Error editing comment:", error);
		}
	};

	const handleDeleteInit = (deleteCommentID: any) => {
		// Handle the Clicking of Delete Button
		setDeleteCommentClick(!deleteCommentClick);

		// Handle the storing of Selected Comment ID
		setDeleteCommentID(deleteCommentID);
	};

	const handleDeleteComment = async (commentId: any) => {
		try {
			// Call the deleteComment API function with the correct commentId
			const data = await deleteComment(currentTenantKey, commentId);

			// Filter out the deleted comment from the state
			const updatedComments = comments.filter(
				(comment) => comment._id !== commentId
			);
			setComments(updatedComments);
		} catch (error) {
			console.error("Error deleting comment: ", error);
		}
	};

	const handleCancelComment = () => {
		setNewComment(""); // Clear the input field
		editorRef.current?.clearEditor(); // Clear the TextEditor content
		setHideButtons(true);
	};

	const [open, setOpen] = useState(false);

	const onClose = () => {
		// Handle new comment case
		if (newComment && newCommentTrimmed.length > 0) {
			setOpen(true);
			return;
		}

		// Handle edit comment case
		if (editCommentClick && editCommentContent) {
			const cleanText = (text: string) =>
				text
					.replace(/<[^>]*>/g, "") // Remove HTML tags
					.replace(/\s+/g, " ") // Replace multiple spaces with single space
					.trim();

			const cleanedEditContent = cleanText(editCommentContent);
			const cleanedOriginalContent = cleanText(commentCompare);

			if (cleanedEditContent !== cleanedOriginalContent) {
				setOpen(true);
				return;
			}
		}

		// If no unsaved changes, close the drawer
		setOpen(false);
		setDiscussionOpen(false);
	};

	useEffect(() => {
		if (!discussionOpen) {
			if (newComment && newCommentTrimmed.length > 0) {
				setNewComment("");
				editorRef.current?.clearEditor();
				console.log("run1");
			}

			if (editCommentClick) {
				/* HandleEditInit */
				setEditCommentClick(false);
				setEditCommentID(null);
				setCommentCompare("");

				editorRef.current?.clearEditor(); // Clear the TextEditor content
				console.log("run2");
			}
		}
	}, [discussionOpen]);

	return (
		<>
			{/* Discussion button */}
			<DiscussionButton setDiscussionOpen={setDiscussionOpen} />
			{/* Discussion drawer */}
			<Drawer
				title={`${title}`}
				open={discussionOpen}
				onClose={onClose}
				closeIcon={GetAntIcon("rightarrow")}
				width={"50vw"}
				zIndex={9999}
				id="deliverable-discussion-modal"
				className="discussion-drawer"
			>
				{/* Add comment container */}
				{task?.closed !== true && deliverable?.status !== "Resolved" && (
					<div className="drawer-content" ref={containerRef}>
						<div className="comment-container">
							<CommenterSection />

							{/* Text Editor */}
							<TextEditor
								ref={editorRef}
								onChange={setNewComment}
								onClick={() => setHideButtons(false)}
								value={newComment}
								disabled={editCommentClick}
							/>

							{/* Buttons Container */}
							{!hideButtons && (
								<div className="button-container">
									<Button
										className="ant-btn-secondary"
										onClick={handleCancelComment}
									>
										Cancel
									</Button>
									<Button
										className="ant-btn-primary"
										onClick={handleAddComment}
										disabled={addButtonDisabled}
									>
										Save
									</Button>
								</div>
							)}
						</div>
					</div>
				)}

				{comments.length > 0
					? comments.map((comment: any) => {
							return (
								<div key={comment._id} className="drawer-content">
									<div className="comment-container">
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
											}}
										>
											<CommenterSection comment={comment} />
											{task?.closed !== true &&
												deliverable?.status !== "Resolved" &&
												emailsEqual(userInfo?.user?.email, comment.user) && (
													<Space>
														<EditCommentButton
															handleEdit={() =>
																handleEditInit(comment._id, comment.comment)
															}
															newCommentTrimmed={newCommentTrimmed}
														/>
														<DeleteCommentButton
															handleDelete={() =>
																handleDeleteComment(comment._id)
															}
														/>
													</Space>
												)}
										</div>
										{editCommentClick && editCommentID === comment._id ? (
											<>
												<TextEditor
													ref={editorRef}
													value={editCommentContent}
													onChange={setEditCommentContent}
												/>
												<div className="button-container">
													<Button
														className="ant-btn-secondary"
														onClick={() => {
															handleEditInit(comment._id, comment.comment);
														}}
													>
														Cancel
													</Button>
													<Button
														className="ant-btn-primary"
														disabled={!editButtonDisabled} // Disable button if content hasn't changed
														onClick={() =>
															handleEditComment(
																editCommentID,
																editCommentContent
															)
														}
													>
														Save
													</Button>
												</div>
											</>
										) : (
											<CommentBox comment={comment} />
										)}
									</div>
								</div>
							);
						})
					: task?.closed === true && (
							<div className="empty-comments-container">
								<Empty
									className="no-data-empty"
									style={{ margin: "auto", fontSize: "20px" }}
									image={elementalSvg}
									description="No comments available."
								/>
							</div>
						)}

				{/* Leave with changes modal */}
				<LeaveConfirmationModal
					open={open}
					setOpen={setOpen}
					discussionOpen={discussionOpen}
					setDiscussionOpen={setDiscussionOpen}
				/>
			</Drawer>
		</>
	);
}

export default DeliverablesDiscussionModal;
